import React from 'react';
import {
  Alert, Box, Button, Chip, CircularProgress, Divider, Drawer, IconButton, Stack, Table, TableBody, TableCell,
  TableHead, TableRow, Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import { useTranslation } from 'react-i18next';
import { getWidgetProvenance } from '../../../utilityFunctions/dashboardApi';
import { CHIP_WRAP_SX, CODE_BOX_SX, CODE_PRE_SX } from '../layoutSx';

const VERDICT_COLOR = { VALID: 'success', DEGRADED: 'warning', FAILED: 'error' };

const Section = ({ title, children }) => (
  <Box sx={{ mb: 2 }}>
    <Typography variant="overline" sx={{ color: 'text.secondary', lineHeight: 1.8 }}>{title}</Typography>
    {children}
  </Box>
);

const Pre = ({ children }) => (
  <Box sx={CODE_BOX_SX}>
    <Typography variant="caption" component="pre" sx={CODE_PRE_SX}>{children}</Typography>
  </Box>
);

/** Verdicts arrive as { stage: { verdict, issues } } (contract) or { stage: "VALID" } (tolerated). */
const normalizeVerdicts = (v) => Object.entries(v || {}).map(([stage, val]) => (
  val && typeof val === 'object'
    ? { stage, verdict: val.verdict, issues: Array.isArray(val.issues) ? val.issues : [] }
    : { stage, verdict: val, issues: [] }
));

/**
 * "How I got this": GET /{id}/widgets/{widgetId}/provenance — the frozen intent,
 * the SQL template with its parameters, tables, per-stage verdicts and metric definition.
 */
export default function ProvenanceDrawer({ open, onClose, dashboardId, widget, versionId, jwt }) {
  const { t } = useTranslation();
  const [state, setState] = React.useState({ loading: false, error: null, data: null });
  const [copied, setCopied] = React.useState(false);
  const widgetId = widget && widget.widget_id;

  React.useEffect(() => {
    if (!open || !widgetId) return undefined;
    let alive = true;
    setState({ loading: true, error: null, data: null });
    setCopied(false);
    getWidgetProvenance(dashboardId, widgetId, versionId, jwt).then((res) => {
      if (!alive) return;
      if (res.ok && res.data) setState({ loading: false, error: null, data: res.data });
      else setState({ loading: false, error: (res.data && res.data.message) || t('Dashboards.provenance.loadFailed'), data: null });
    });
    return () => { alive = false; };
  }, [open, dashboardId, widgetId, versionId, jwt, t]);

  const copySql = async () => {
    try {
      await navigator.clipboard.writeText(state.data.sql_template || '');
      setCopied(true);
    } catch (e) {
      setCopied(false);
    }
  };

  const p = state.data;
  const params = (p && Array.isArray(p.parameters)) ? p.parameters : [];
  const tables = (p && Array.isArray(p.tables)) ? p.tables : [];
  const verdicts = normalizeVerdicts(p && p.verdicts);
  const metric = p && p.metric_definition;

  return (
    <Drawer anchor="right" open={open} onClose={onClose} PaperProps={{ sx: { width: { xs: '100%', sm: 520 }, maxWidth: '100%' } }}>
      <Box sx={{ display: 'flex', alignItems: 'center', px: 2, py: 1.5, gap: 1 }}>
        <Box sx={{ flexGrow: 1, minWidth: 0 }}>
          <Typography variant="h6" sx={{ fontWeight: 700 }}>{t('Dashboards.provenance.title')}</Typography>
          {widget && <Typography variant="body2" color="text.secondary" noWrap>{widget.title || widget.widget_id}</Typography>}
        </Box>
        <IconButton onClick={onClose} aria-label={t('Dashboards.common.close')}><CloseIcon /></IconButton>
      </Box>
      <Divider />
      <Box sx={{ p: 2, overflowY: 'auto', minWidth: 0, overflowWrap: 'anywhere' }}>
        {state.loading && <Box sx={{ p: 4, textAlign: 'center' }}><CircularProgress /></Box>}
        {state.error && <Alert severity="error">{state.error}</Alert>}
        {p && (
          <>
            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 2 }}>
              {p.source && (
                <Chip size="small" color="primary" variant="outlined"
                  label={t(`Dashboards.provenance.sources.${p.source}`, { defaultValue: p.source })} />
              )}
              {p.version_no != null && <Chip size="small" variant="outlined" label={t('Dashboards.provenance.version', { n: p.version_no })} />}
            </Box>

            {p.intent && (
              <Section title={t('Dashboards.provenance.intent')}>
                <Pre>{JSON.stringify(p.intent, null, 2)}</Pre>
              </Section>
            )}

            {p.sql_template && (
              <Section title={t('Dashboards.provenance.sql')}>
                <Pre>{p.sql_template}</Pre>
                <Button size="small" startIcon={<ContentCopyIcon fontSize="small" />} onClick={copySql} sx={{ mt: 0.5, textTransform: 'none' }}>
                  {copied ? t('Dashboards.common.copied') : t('Dashboards.provenance.copySql')}
                </Button>
              </Section>
            )}

            <Section title={t('Dashboards.provenance.parameters')}>
              {params.length === 0 ? (
                <Typography variant="body2" color="text.secondary">{t('Dashboards.provenance.noParameters')}</Typography>
              ) : (
                <Box sx={{ overflowX: 'auto', maxWidth: '100%' }}>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>name</TableCell>
                        <TableCell>type</TableCell>
                        <TableCell>column</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {params.map((x) => (
                        <TableRow key={x.name}>
                          <TableCell sx={{ fontFamily: 'monospace', wordBreak: 'break-all' }}>:{x.name}{x.required ? ' *' : ''}</TableCell>
                          <TableCell>{x.type}</TableCell>
                          <TableCell sx={{ fontFamily: 'monospace', fontSize: 12, wordBreak: 'break-all' }}>{x.column || '—'}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </Box>
              )}
            </Section>

            {tables.length > 0 && (
              <Section title={t('Dashboards.provenance.tables')}>
                <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                  {tables.map((tb) => <Chip key={tb} size="small" variant="outlined" label={tb} sx={{ ...CHIP_WRAP_SX, fontFamily: 'monospace' }} />)}
                </Box>
              </Section>
            )}

            {verdicts.length > 0 && (
              <Section title={t('Dashboards.provenance.verdicts')}>
                <Stack spacing={0.5}>
                  {verdicts.map((v) => (
                    <Box key={v.stage}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                        <Chip size="small" color={VERDICT_COLOR[v.verdict] || 'default'} label={v.verdict || '—'} />
                        <Typography variant="body2" sx={{ minWidth: 0 }}>{v.stage}</Typography>
                      </Box>
                      {v.issues.map((iss, i) => (
                        <Typography key={i} variant="caption" color="text.secondary" display="block" sx={{ pl: 2 }}>• {iss}</Typography>
                      ))}
                    </Box>
                  ))}
                </Stack>
              </Section>
            )}

            {metric && (
              <Section title={t('Dashboards.provenance.metric')}>
                <Stack spacing={0.25}>
                  {Object.entries(metric).filter(([, val]) => val !== null && val !== undefined
                    && !(Array.isArray(val) && val.length === 0)).map(([k, val]) => (
                      <Box key={k} sx={{ display: 'flex', gap: 1, minWidth: 0 }}>
                        <Typography variant="caption" sx={{ color: 'text.disabled', width: 110, flexShrink: 0 }}>{k}</Typography>
                        <Typography variant="caption" sx={{ fontFamily: 'monospace', wordBreak: 'break-all', minWidth: 0 }}>
                          {typeof val === 'object' ? JSON.stringify(val) : String(val)}
                        </Typography>
                      </Box>
                  ))}
                </Stack>
              </Section>
            )}
          </>
        )}
      </Box>
    </Drawer>
  );
}
